import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { confirmAlert } from "react-confirm-alert";
import {
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Business as BusinessIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { FirebaseAuth, Firestore } from "../FirebaseAuth/firebase";
import {
  LOCAL_STORAGE_CURRENT_ORG,
  setLocalStorage,
  getLocalStorage,
} from "../../utils/localStorage";

/**
 * Organization switcher dropdown component
 * Displays current organization and allows switching between available organizations
 */
const OrganizationSwitcher = () => {
  const isDataFetchedRef = useRef(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentOrganization =
    getLocalStorage(LOCAL_STORAGE_CURRENT_ORG) || null;
  const history = useNavigate();

  const open = Boolean(anchorEl);

  // Fetch user's organizations on mount
  useEffect(() => {
    if (isDataFetchedRef.current) return;
    isDataFetchedRef.current = true;

    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        const userId = FirebaseAuth.currentUser?.uid;

        if (!userId) {
          setLoading(false);
          return;
        }

        const organizationsRef = query(
          collection(Firestore, "organizations"),
          where("access", "array-contains", userId)
        );

        const snapshot = await getDocs(organizationsRef);

        if (snapshot.empty) {
          setOrganizations([]);
          setLoading(false);
          return;
        }

        const orgs = [];
        snapshot.forEach((doc) => {
          orgs.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setOrganizations(orgs);
      } catch (error) {
        console.error("Error fetching organizations:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSwitchOrganization = (organization) => {
    handleClose();

    confirmAlert({
      title: "Switch Organization",
      message: `Are you sure you want to set '${organization.name}' as your active organization?`,
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            setLocalStorage(LOCAL_STORAGE_CURRENT_ORG, organization);
            window.location.reload(); // Reload to apply changes throughout the app
          },
          className: "react-confirm-alert-button-delete",
        },
        {
          label: "No",
          className: "react-confirm-alert-button-cancel",
          onClick: () => {},
        },
      ],
    });
  };

  const handleCreateOrganization = () => {
    handleClose();
    history("/new-organization");
  };

  const handleManageOrganizations = () => {
    handleClose();
    history("/organization/switch-organization");
  };

  // Get initials for the avatar
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Determine organization color or use a default
  const getOrgColor = () => {
    if (currentOrganization?.primaryColor) {
      return currentOrganization.primaryColor;
    }
    return "#1976d2"; // Default to primary blue
  };

  return (
    <Box>
      <Tooltip title="Switch organization">
        <Button
          onClick={handleClick}
          color="inherit"
          sx={{
            display: "flex",
            alignItems: "center",
            borderRadius: 2,
            px: 2,
            py: 1,
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.15)",
            },
            minWidth: 100,
          }}
          endIcon={open ? <ArrowUpIcon /> : <ArrowDownIcon />}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  mr: 1,
                  bgcolor: getOrgColor(),
                  fontSize: "0.875rem",
                }}
              >
                {currentOrganization ? (
                  getInitials(currentOrganization.name)
                ) : (
                  <BusinessIcon fontSize="small" />
                )}
              </Avatar>
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 120,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "left",
                }}
              >
                {currentOrganization?.name || "Select Org"}
              </Typography>
            </>
          )}
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 250,
            maxHeight: 400,
            overflow: "auto",
            mt: 1.5,
            borderRadius: 2,
            border: "1px solid rgba(0, 0, 0, 0.05)",
          },
        }}
        transformOrigin={{ horizontal: "center", vertical: "top" }}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Your Organizations
          </Typography>
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : organizations.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No organizations available
            </Typography>
          </MenuItem>
        ) : (
          organizations.map((org) => (
            <MenuItem
              key={org.id}
              onClick={() => handleSwitchOrganization(org)}
              selected={currentOrganization?.id === org.id}
              sx={{
                py: 1.5,
                mx: 1,
                my: 0.5,
                borderRadius: 1,
                "&.Mui-selected": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                },
              }}
            >
              <ListItemIcon>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: org.primaryColor || "#1976d2",
                    fontSize: "0.875rem",
                  }}
                >
                  {getInitials(org.name)}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={org.name}
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: currentOrganization?.id === org.id ? 600 : 400,
                }}
              />
            </MenuItem>
          ))
        )}

        <Divider />
        <MenuItem onClick={handleCreateOrganization} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Create New Organization"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </MenuItem>
        <MenuItem onClick={handleManageOrganizations} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <BusinessIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Manage Organizations"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default OrganizationSwitcher;
